import React, {
  createContext,
  useContext,
  useEffect,
  useState,
  useRef,
} from 'react';
import Bugsnag from '@bugsnag/js';
import BugsnagPluginReact from '@bugsnag/plugin-react';
import axios, { AxiosError } from 'axios';

import { CONSTANTS } from 'src/shared/constants';
import { Navigate, useLocation, useNavigate } from 'react-router-dom';
import { useUserStore } from 'src/shared/stores/useUserStore';

type BugsnagClient = typeof Bugsnag | null;
const BugsnagContext = createContext<BugsnagClient>(null);
export const useBugsnag = () => useContext(BugsnagContext);

function isFastcompPath(pathname: string): boolean {
  return pathname === '/fastcomp' || pathname.startsWith('/fastcomp/');
}

class SimpleErrorBoundary extends React.Component<
  { children: React.ReactNode; onError?: () => void },
  { shouldRedirect: boolean }
> {
  state = { shouldRedirect: false };

  static getDerivedStateFromError() {
    return { shouldRedirect: true };
  }

  componentDidCatch(error: unknown) {
    // eslint-disable-next-line no-console
    console.error('[ErrorBoundary] simple boundary triggered', error);
    this.props.onError?.();
    if (typeof window === 'undefined') return;
    const currentPath = window.location?.pathname;
    if (currentPath === '/error' || isFastcompPath(currentPath || '')) return;
    const lastRoute = sessionStorage.getItem('last_route');
    sessionStorage.setItem('error_route', lastRoute || currentPath || '/home');
  }

  render() {
    if (this.state.shouldRedirect) {
      if (
        typeof window !== 'undefined' &&
        (window.location?.pathname === '/error' ||
          isFastcompPath(window.location?.pathname || ''))
      ) {
        return null;
      }
      return <Navigate to="/error" replace />;
    }
    return this.props.children;
  }
}

export function BugsnagProvider({ children }: { children: React.ReactNode }) {
  const apiKey = CONSTANTS.BUGSNAG_API_KEY || '';
  const releaseStage = CONSTANTS.APP_ENV;
  const appVersion = '1.0.0';

  const shouldInit = Boolean(apiKey && releaseStage);

  const [client, setClient] = useState<BugsnagClient>(null);
  const [ErrorBoundary, setErrorBoundary] =
    useState<React.ComponentType<any> | null>(null);
  const [failedPath, setFailedPath] = useState<string | null>(null);

  const metaRef = useRef({
    userId: '',
    email: '',
    tenantId: '',
    path: '',
    isFastcomp: false,
  });

  const { pathname } = useLocation();
  const isFastcomp = isFastcompPath(pathname);
  const legacyIdentity = useUserStore((state) =>
    isFastcomp
      ? null
      : { userId: state.userId, email: state.email, tenantId: state.tenantId },
  );
  const userId = legacyIdentity?.userId || '';
  const email = legacyIdentity?.email || '';
  const tenantId = legacyIdentity?.tenantId || '';

  useEffect(() => {
    if (!shouldInit || client) return;

    Bugsnag.start({
      apiKey,
      releaseStage,
      appVersion,
      plugins: [new BugsnagPluginReact()],
      onError(event) {
        // console.log('###OPA ERROR ####', event);
        const errorClass = event.errors?.[0]?.errorClass?.toLowerCase() ?? '';
        const errorMessage =
          event.errors?.[0]?.errorMessage?.toLowerCase() ?? '';
        const originalError = event.originalError?.toLowerCase() || '';

        const skipMatchMessages = [
          'ResizeObserver loop limit exceeded'.toLowerCase(),
          'ResizeObserver loop completed with undelivered notifications.'.toLowerCase(),
        ];

        let shouldSkip = false;

        skipMatchMessages.forEach((message) => {
          if (
            errorClass === message ||
            errorMessage.includes(message) ||
            originalError === message
          ) {
            shouldSkip = true;
          }
        });

        if (shouldSkip) {
          return false;
        }

        const err = event.originalError;

        if (axios.isAxiosError(err)) {
          const axErr = err as AxiosError<any>;

          const method = axErr.config?.method?.toUpperCase() ?? 'HTTP';
          event.context = `${method} ${axErr.config?.url}`;

          event.addMetadata('axios', {
            url: axErr.config?.url,
            baseURL: axErr.config?.baseURL,
            fullUrl: `${axErr.config?.baseURL}${axErr.config?.url}`,
            method: axErr.config?.method,
            status: axErr.response?.status,
            statusText: axErr.response?.statusText,
            requestData: axErr.config?.data,
            responseData: axErr.response?.data,
            headers: axErr.response?.headers,
            message: axErr.message,
          });

          /* (Opcional) ignorar 401 não desejados
          if (axErr.response?.status === 401) return false;
          */
        }
      },
    });

    setClient(Bugsnag);

    const plugin = Bugsnag.getPlugin('react') as any;
    if (plugin?.createErrorBoundary) {
      const ErrorFallback = () => {
        const location = useLocation();
        const navigate = useNavigate();
        useEffect(() => {
          setFailedPath((current) => current ?? location.pathname);
          // eslint-disable-next-line no-console
          console.error('[ErrorBoundary] triggered', {
            pathname: location.pathname,
          });
          if (
            location.pathname === '/error' ||
            isFastcompPath(location.pathname)
          )
            return;
          const lastRoute = sessionStorage.getItem('last_route');
          sessionStorage.setItem(
            'error_route',
            lastRoute || location.pathname || '/home',
          );
          navigate('/error', { replace: true });
        }, [location.pathname, navigate]);
        return null;
      };
      const Boundary = plugin.createErrorBoundary(React, {
        FallbackComponent: ErrorFallback,
      });
      setErrorBoundary(() => Boundary);
    }
  }, [shouldInit, client, apiKey, releaseStage, appVersion]);

  useEffect(() => {
    if (!client) return;

    if (isFastcomp) {
      if (!metaRef.current.isFastcomp) {
        client.setUser();
        const clearMetadata = (
          client as typeof client & {
            clearMetadata?: (section: string) => void;
          }
        ).clearMetadata;
        clearMetadata?.('tenant');
      }
      metaRef.current.userId = '';
      metaRef.current.email = '';
      metaRef.current.tenantId = '';
      metaRef.current.isFastcomp = true;
    } else {
      metaRef.current.isFastcomp = false;

      if (
        metaRef.current.userId !== userId ||
        metaRef.current.email !== email
      ) {
        client.setUser(
          userId || undefined,
          email || undefined,
          /* nome (opcional) */ undefined,
        );
        metaRef.current.userId = userId;
        metaRef.current.email = email;
      }

      if (metaRef.current.tenantId !== tenantId) {
        client.addMetadata('tenant', { id: tenantId });
        metaRef.current.tenantId = tenantId;
      }
    }

    if (metaRef.current.path !== pathname) {
      client.addMetadata('route', { path: pathname });
      metaRef.current.path = pathname;
    }

    if (pathname && pathname !== '/error') {
      sessionStorage.setItem('last_route', pathname);
    }
  }, [client, userId, email, tenantId, pathname, isFastcomp]);

  const isFastcompAppPath =
    pathname === '/fastcomp/app' || pathname.startsWith('/fastcomp/app/');
  const isRecovering = Boolean(failedPath && failedPath !== pathname);
  const boundaryKey = isFastcompAppPath
    ? isRecovering
      ? `/fastcomp/app:recover:${pathname}`
      : '/fastcomp/app'
    : pathname;

  useEffect(() => {
    if (isRecovering) setFailedPath(null);
  }, [isRecovering]);

  return (
    <BugsnagContext.Provider value={client}>
      {ErrorBoundary ? (
        <ErrorBoundary key={boundaryKey}>{children}</ErrorBoundary>
      ) : (
        <SimpleErrorBoundary
          key={boundaryKey}
          onError={() => setFailedPath((current) => current ?? pathname)}
        >
          {children}
        </SimpleErrorBoundary>
      )}
    </BugsnagContext.Provider>
  );
}
